import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Package, ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import { BundleDetailSheet } from './BundleDetailSheet';
import { trackBundleView, trackBundleAddToCart } from '../../utils/tracking';

export const BundleCarousel = () => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBundles = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundles`);
        const data = await response.json();
        
        // Fetch details for each bundle to get products
        const bundlesWithProducts = await Promise.all(
          (data.bundles || []).map(async (bundle) => {
            try {
              const detailRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bundles/${bundle.id}`);
              const detailData = await detailRes.json();
              return detailData;
            } catch (err) {
              return bundle;
            }
          })
        );
        
        setBundles(bundlesWithProducts);
      } catch (error) {
        console.error('Failed to fetch bundles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBundles();
  }, []);

  const handleBundleClick = (bundle) => {
    setSelectedBundle(bundle.id);
    setDetailOpen(true);
    trackBundleView(bundle.id, bundle.name);
  };

  const handleAddBundle = async (bundle, e) => {
    e.stopPropagation();
    
    try {
      // Bundle already has products loaded
      if (bundle.products && bundle.products.length > 0) {
        // Add bundle as single cart item with bundle pricing
        const bundleCartItem = {
          id: bundle.id,
          title: bundle.name,
          description: bundle.description,
          price: bundle.price,
          original_price: bundle.original_price,
          savings: bundle.savings,
          image_urls: bundle.products.map(p => p.image_urls?.[0]).filter(Boolean),
          isBundle: true,
          bundleProducts: bundle.products,
          productCount: bundle.products.length
        };
        
        addToCart(bundleCartItem);
        trackBundleAddToCart(bundle.id, bundle.name, bundle.price);
        toast.success(`${bundle.name} added to cart!`, {
          description: `Bundle with ${bundle.products.length} items - ₹${bundle.price}`
        });
      } else {
        toast.info('Bundle coming soon!', {
          description: 'Products will be added shortly'
        });
      }
    } catch (error) {
      console.error('Failed to add bundle:', error);
      toast.error('Failed to add bundle');
    }
  };

  if (loading || bundles.length === 0) return null;

  return (
    <section className="py-5 sm:py-7" data-testid="bundles-section">
      <div className="container mx-auto px-4">
        <div className="flex items-end justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-500" />
            <h3 className="text-lg sm:text-xl font-heading font-semibold">Curated Bundles</h3>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-primary hover:underline flex items-center gap-1" 
            onClick={() => navigate('/bundles')}
            data-testid="view-all-bundles-link"
          >
            See all
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Save more with our ready-made kits</p>

        <div 
          className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
          data-testid="bundles-rail"
        >
          {bundles.map((bundle) => (
            <div key={bundle.id} className="w-[280px] sm:w-[320px] flex-shrink-0 snap-start">
              <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
                <Card 
                  className="overflow-hidden border-2 border-primary/20 hover:border-primary/40 hover:shadow-lg transition-all cursor-pointer h-full"
                  onClick={() => handleBundleClick(bundle)}
                  data-testid="bundle-card"
                >
                  <CardContent className="p-0">
                    <div className="relative">
                      {/* Product Image Collage */}
                      {bundle.products && bundle.products.length > 0 ? (
                        <div className="grid grid-cols-2 gap-1 h-40 md:h-48 bg-muted">
                          {bundle.products.slice(0, 4).map((product, idx) => (
                            <div 
                              key={idx} 
                              className={`overflow-hidden ${
                                bundle.products.length === 1 ? 'col-span-2' :
                                bundle.products.length === 3 && idx === 0 ? 'col-span-2' : ''
                              }`}
                            >
                              <img
                                src={product.image_urls?.[0] || bundle.image_url}
                                alt={product.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <img
                          src={bundle.image_url}
                          alt={bundle.name}
                          className="w-full h-40 md:h-48 object-cover"
                          loading="lazy"
                        />
                      )}
                      <div className="absolute top-2 right-2">
                        <Badge className="bg-green-500 text-white border-0">
                          Save ₹{bundle.savings}
                        </Badge>
                      </div>
                      <div className="absolute top-2 left-2">
                        <Badge variant="secondary" className="bg-white/90 backdrop-blur">
                          <Sparkles className="w-3 h-3 mr-1" />
                          Bundle
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="p-4 space-y-3">
                      <div>
                        <h4 className="font-semibold text-base line-clamp-1">{bundle.name}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {bundle.description}
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-lg font-bold text-primary">₹{bundle.price}</span>
                            <span className="text-sm text-muted-foreground line-through">₹{bundle.original_price}</span>
                          </div>
                          <p className="text-xs text-green-600 font-semibold">
                            {Math.round((bundle.savings / bundle.original_price) * 100)}% OFF
                          </p>
                        </div>
                        
                        <Button
                          size="sm"
                          onClick={(e) => handleAddBundle(bundle, e)}
                          className="bg-primary hover:bg-primary/90"
                          data-testid="add-bundle-button"
                        >
                          Add Bundle
                        </Button>
                      </div>
                      
                      {bundle.age_groups && bundle.age_groups.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {bundle.age_groups.map((age) => (
                            <Badge key={age} variant="outline" className="text-xs px-1.5 py-0">
                              {age} yrs
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      {/* Bundle Detail Sheet */}
      <BundleDetailSheet 
        bundleId={selectedBundle}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </section>
  );
};
